// src/components/clientadmin/pages/ClientSettings.js
import React, { useState, useEffect } from "react";
import { useTheme } from "../../../context/ThemeContext";
import { useAuth } from "../../../context/AuthContext";
import { updatePassword, reauthenticateWithCredential, EmailAuthProvider } from "firebase/auth";
import { doc, updateDoc, getDoc } from "firebase/firestore";
import { db, auth } from "../../../firebase/config";
import { 
  User, Lock, Bell, Palette, Save, Eye, EyeOff, 
  Camera, Mail, Phone, CheckCircle, AlertCircle, ArrowLeft 
} from "lucide-react";
import { Link, useParams } from "react-router-dom";

const ClientSettings = () => {
  const { user } = useAuth();
  const { clientId } = useParams();
  
  console.log("🎯 ClientSettings loaded for clientId:", clientId);
  
  // Solution de fallback si ThemeContext n'existe pas
  const themeContext = useTheme();
  const [localTheme, setLocalTheme] = useState("light");

  const theme = themeContext?.theme || localTheme;
  const setTheme = themeContext?.setTheme || ((newTheme) => {
    console.log("🎨 Changement de thème local:", newTheme);
    setLocalTheme(newTheme);
    // Appliquer le thème au document
    document.documentElement.setAttribute('data-theme', newTheme);
    // Appliquer les classes CSS du thème
    if (newTheme === 'dark') {
      document.documentElement.classList.add('dark');
      document.body.style.backgroundColor = '#1f2937';
      document.body.style.color = '#f9fafb';
    } else {
      document.documentElement.classList.remove('dark');
      document.body.style.backgroundColor = '';
      document.body.style.color = '';
    }
  });

  // Debug du thème
  console.log("🎨 Thème actuel:", theme);
  console.log("🎨 ThemeContext:", themeContext);

  // États pour les sections
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  
  // États pour le profil
  const [profileData, setProfileData] = useState({
    displayName: "",
    email: "",
    phone: "",
    role: ""
  });

  // États pour le changement de mot de passe
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  // États pour les notifications
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    smsNotifications: false,
    pushNotifications: true,
    reminderDays: 3
  });

  // Charger les données utilisateur
  useEffect(() => {
    const loadUserData = async () => {
      if (!user?.uid) return;
      
      try {
        setLoading(true);
        const userDoc = await getDoc(doc(db, "users", user.uid));
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setProfileData({
            displayName: userData.displayName || user.displayName || "",
            email: user.email || "",
            phone: userData.phone || "",
            role: userData.role || ""
          });
          
          setNotificationSettings({
            emailNotifications: userData.emailNotifications !== false,
            smsNotifications: userData.smsNotifications || false,
            pushNotifications: userData.pushNotifications !== false,
            reminderDays: userData.reminderDays || 3
          });
        }
      } catch (err) {
        setError("Erreur lors du chargement des données");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, [user]);

  // Sauvegarder le profil - VERSION CORRIGÉE
  const handleSaveProfile = async () => {
    if (!user?.uid) {
      setError("Utilisateur non connecté");
      return;
    }
    
    try {
      setLoading(true);
      setError("");
      setSuccess("");

      console.log("🔄 Sauvegarde du profil - Solution contournement");

      // SEULEMENT Firestore (bypass du problème account_info.ts)
      await updateDoc(doc(db, "users", user.uid), {
        displayName: profileData.displayName,
        phone: profileData.phone,
        updatedAt: new Date()
      });

      console.log("✅ Profil sauvegardé dans Firestore!");
      setSuccess("Profil mis à jour avec succès !");
      setTimeout(() => setSuccess(""), 3000);
      
    } catch (err) {
      console.error("❌ Erreur:", err);
      setError("Erreur lors de la mise à jour du profil: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Changer le mot de passe - VERSION CORRIGÉE
  const handleChangePassword = async () => {
    // Utiliser auth.currentUser au lieu du user du contexte
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      setError("Utilisateur non connecté");
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError("Les nouveaux mots de passe ne correspondent pas");
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setError("Le nouveau mot de passe doit contenir au moins 6 caractères");
      return;
    }

    if (!passwordData.currentPassword) {
      setError("Veuillez entrer votre mot de passe actuel");
      return;
    }

    try {
      setLoading(true);
      setError("");
      setSuccess("");

      console.log("🔐 Début du changement de mot de passe");
      console.log("👤 Current user:", currentUser);
      console.log("📧 Email:", currentUser.email);

      // Étape 1: Réauthentifier l'utilisateur avec son mot de passe actuel
      const credential = EmailAuthProvider.credential(currentUser.email, passwordData.currentPassword);
      
      console.log("🔑 Réauthentification...");
      await reauthenticateWithCredential(currentUser, credential);
      console.log("✅ Réauthentification réussie");

      // Étape 2: Changer le mot de passe
      console.log("🔄 Changement du mot de passe...");
      await updatePassword(currentUser, passwordData.newPassword);
      console.log("✅ Mot de passe changé avec succès");
      
      // Vider les champs
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
      });

      setSuccess("Mot de passe modifié avec succès !");
      setTimeout(() => setSuccess(""), 3000);

    } catch (err) {
      console.error("❌ Erreur changement mot de passe:", err);
      
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setError("Le mot de passe actuel est incorrect");
      } else if (err.code === 'auth/weak-password') {
        setError("Le nouveau mot de passe est trop faible");
      } else if (err.code === 'auth/requires-recent-login') {
        setError("Veuillez vous déconnecter et vous reconnecter avant de changer votre mot de passe");
      } else if (err.code === 'auth/too-many-requests') {
        setError("Trop de tentatives. Veuillez réessayer dans quelques minutes");
      } else {
        setError("Erreur lors du changement de mot de passe: " + err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // Sauvegarder les préférences de notifications
  const handleSaveNotifications = async () => {
    if (!user?.uid) {
      setError("Utilisateur non connecté");
      return;
    }
    
    try {
      setLoading(true);
      setError("");
      setSuccess("");

      console.log("💾 Sauvegarde notifications:", notificationSettings);

      await updateDoc(doc(db, "users", user.uid), {
        emailNotifications: notificationSettings.emailNotifications,
        smsNotifications: notificationSettings.smsNotifications,
        pushNotifications: notificationSettings.pushNotifications,
        reminderDays: notificationSettings.reminderDays,
        updatedAt: new Date()
      });

      console.log("✅ Notifications sauvegardées!");
      setSuccess("Préférences de notifications sauvegardées avec succès !");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error("❌ Erreur sauvegarde notifications:", err);
      setError("Erreur lors de la sauvegarde des notifications: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Toggle password visibility
  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  return (
    <div className={`min-h-screen p-6 transition-colors duration-300 ${
      theme === 'dark' 
        ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white'
        : 'bg-gradient-to-br from-blue-50 via-white to-purple-50'
    }`}>
      <div className="max-w-4xl mx-auto">
        {/* Header avec bouton retour */}
        <div className="mb-8 flex items-center">
          <Link 
            to={`/client/${clientId}`}
            className={`mr-4 p-2 rounded-full hover:bg-opacity-10 hover:bg-gray-500 transition-colors ${
              theme === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <ArrowLeft className="w-6 h-6" />
          </Link>
          <div>
            <h1 className={`text-4xl font-bold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              Paramètres
            </h1>
            <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
              Gérez votre profil et vos préférences
            </p>
          </div>
        </div>

        {/* Messages de feedback */}
        {success && (
          <div className={`mb-6 p-4 border-l-4 border-green-400 rounded-lg ${
            theme === 'dark' ? 'bg-green-900 bg-opacity-50' : 'bg-green-50'
          }`}>
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
              <p className={`${theme === 'dark' ? 'text-green-300' : 'text-green-700'}`}>
                {success}
              </p>
            </div>
          </div>
        )}

        {error && (
          <div className={`mb-6 p-4 border-l-4 border-red-400 rounded-lg ${
            theme === 'dark' ? 'bg-red-900 bg-opacity-50' : 'bg-red-50'
          }`}>
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-red-400 mr-3" />
              <p className={`${theme === 'dark' ? 'text-red-300' : 'text-red-700'}`}>
                {error}
              </p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Section Profil */}
          <div className={`rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 ${
            theme === 'dark' 
              ? 'bg-gray-800 text-white' 
              : 'bg-white'
          }`}>
            <div className="flex items-center mb-6">
              <div className={`p-3 rounded-full mr-4 ${
                theme === 'dark' ? 'bg-blue-900' : 'bg-blue-100'
              }`}>
                <User className="w-6 h-6 text-blue-600" />
              </div>
              <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                Profil Utilisateur
              </h2>
            </div>

            <div className="space-y-6">
              {/* Photo de profil */}
              <div className="flex items-center space-x-4">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                  {profileData.displayName?.charAt(0)?.toUpperCase() || "U"}
                </div>
                <button className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                  theme === 'dark' 
                    ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                    : 'bg-gray-100 hover:bg-gray-200'
                }`}>
                  <Camera className="w-4 h-4 mr-2" />
                  Changer la photo
                </button>
              </div>

              {/* Nom */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Nom complet
                </label>
                <input
                  type="text"
                  value={profileData.displayName}
                  onChange={(e) => setProfileData(prev => ({
                    ...prev,
                    displayName: e.target.value
                  }))}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                    theme === 'dark' 
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                      : 'border-gray-300 bg-white'
                  }`}
                  placeholder="Votre nom complet"
                />
              </div>

              {/* Email */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Adresse email
                </label>
                <div className="relative">
                  <Mail className={`absolute left-3 top-3 w-5 h-5 ${
                    theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                  }`} />
                  <input
                    type="email"
                    value={profileData.email}
                    disabled
                    className={`w-full pl-12 pr-4 py-3 border rounded-lg ${
                      theme === 'dark' 
                        ? 'bg-gray-700 border-gray-600 text-gray-400' 
                        : 'border-gray-300 bg-gray-50 text-gray-500'
                    }`}
                  />
                </div>
              </div>

              {/* Téléphone */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Téléphone
                </label>
                <div className="relative">
                  <Phone className={`absolute left-3 top-3 w-5 h-5 ${
                    theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                  }`} />
                  <input
                    type="tel"
                    value={profileData.phone}
                    onChange={(e) => setProfileData(prev => ({
                      ...prev,
                      phone: e.target.value
                    }))}
                    className={`w-full pl-12 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      theme === 'dark' 
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                        : 'border-gray-300 bg-white'
                    }`}
                    placeholder="+33 6 12 34 56 78"
                  />
                </div>
              </div>

              {/* Rôle */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Rôle
                </label>
                <input
                  type="text"
                  value={profileData.role}
                  disabled
                  className={`w-full px-4 py-3 border rounded-lg ${
                    theme === 'dark' 
                      ? 'bg-gray-700 border-gray-600 text-gray-400' 
                      : 'border-gray-300 bg-gray-50 text-gray-500'
                  }`}
                />
              </div>

              <button
                onClick={handleSaveProfile}
                disabled={loading}
                className="w-full flex items-center justify-center px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all disabled:opacity-50"
              >
                <Save className="w-5 h-5 mr-2" />
                {loading ? "Sauvegarde..." : "Sauvegarder le profil"}
              </button>
            </div>
          </div>

          {/* Section Sécurité */}
          <div className={`rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 ${
            theme === 'dark' 
              ? 'bg-gray-800 text-white' 
              : 'bg-white'
          }`}>
            <div className="flex items-center mb-6">
              <div className={`p-3 rounded-full mr-4 ${
                theme === 'dark' ? 'bg-red-900' : 'bg-red-100'
              }`}>
                <Lock className="w-6 h-6 text-red-600" />
              </div>
              <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                Sécurité
              </h2>
            </div>

            <div className="space-y-6">
              {/* Mot de passe actuel */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Mot de passe actuel
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.current ? "text" : "password"}
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData(prev => ({
                      ...prev,
                      currentPassword: e.target.value
                    }))}
                    className={`w-full px-4 py-3 pr-12 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all ${
                      theme === 'dark' 
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                        : 'border-gray-300 bg-white'
                    }`}
                    placeholder="Entrez votre mot de passe actuel"
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('current')}
                    className={`absolute right-3 top-3 ${
                      theme === 'dark' ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'
                    }`}
                  >
                    {showPasswords.current ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Nouveau mot de passe */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Nouveau mot de passe
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.new ? "text" : "password"}
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData(prev => ({
                      ...prev,
                      newPassword: e.target.value
                    }))}
                    className={`w-full px-4 py-3 pr-12 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all ${
                      theme === 'dark' 
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                        : 'border-gray-300 bg-white'
                    }`}
                    placeholder="Entrez votre nouveau mot de passe"
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('new')}
                    className={`absolute right-3 top-3 ${
                      theme === 'dark' ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'
                    }`}
                  >
                    {showPasswords.new ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Confirmer le mot de passe */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Confirmer le nouveau mot de passe
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.confirm ? "text" : "password"}
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData(prev => ({
                      ...prev,
                      confirmPassword: e.target.value
                    }))}
                    className={`w-full px-4 py-3 pr-12 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all ${
                      theme === 'dark' 
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                        : 'border-gray-300 bg-white'
                    }`}
                    placeholder="Confirmez votre nouveau mot de passe"
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('confirm')}
                    className={`absolute right-3 top-3 ${
                      theme === 'dark' ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'
                    }`}
                  >
                    {showPasswords.confirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <button
                onClick={handleChangePassword}
                disabled={loading || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
                className="w-full flex items-center justify-center px-6 py-3 bg-gradient-to-r from-red-500 to-pink-600 text-white rounded-lg hover:from-red-600 hover:to-pink-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-all disabled:opacity-50"
              >
                <Lock className="w-5 h-5 mr-2" />
                {loading ? "Changement..." : "Changer le mot de passe"}
              </button>
            </div>
          </div>

          {/* Section Apparence */}
          <div className={`rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 ${
            theme === 'dark' 
              ? 'bg-gray-800 text-white' 
              : 'bg-white'
          }`}>
            <div className="flex items-center mb-6">
              <div className={`p-3 rounded-full mr-4 ${
                theme === 'dark' ? 'bg-purple-900' : 'bg-purple-100'
              }`}>
                <Palette className="w-6 h-6 text-purple-600" />
              </div>
              <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                Apparence
              </h2>
            </div>

            <div className="space-y-6">
              <div>
                <label className={`block text-sm font-medium mb-4 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Thème de l'interface
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => setTheme("light")}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      theme === "light"
                        ? 'border-purple-500 bg-purple-50'
                        : theme === 'dark'
                        ? 'border-gray-600 hover:border-purple-400 bg-gray-700'
                        : 'border-gray-200 hover:border-purple-300'
                    }`}
                  >
                    <div className="w-full h-8 bg-white rounded mb-2 border"></div>
                    <p className={`text-sm font-medium ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                      Clair
                    </p>
                  </button>
                  <button
                    onClick={() => setTheme("dark")}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      theme === "dark"
                        ? 'border-purple-500 bg-purple-900 bg-opacity-50'
                        : 'border-gray-200 hover:border-purple-300'
                    }`}
                  >
                    <div className="w-full h-8 bg-gray-800 rounded mb-2"></div>
                    <p className={`text-sm font-medium ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                      Sombre
                    </p>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Section Notifications */}
          <div className={`rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 ${
            theme === 'dark' 
              ? 'bg-gray-800 text-white' 
              : 'bg-white'
          }`}>
            <div className="flex items-center mb-6">
              <div className={`p-3 rounded-full mr-4 ${
                theme === 'dark' ? 'bg-green-900' : 'bg-green-100'
              }`}>
                <Bell className="w-6 h-6 text-green-600" />
              </div>
              <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                Notifications
              </h2>
            </div>

            <div className="space-y-6">
              {/* Email */}
              <div className="flex items-center justify-between">
                <div>
                  <h3 className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    Notifications par email
                  </h3>
                  <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    Recevez des emails pour les mises à jour importantes
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notificationSettings.emailNotifications}
                    onChange={(e) => setNotificationSettings(prev => ({
                      ...prev,
                      emailNotifications: e.target.checked
                    }))}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                </label>
              </div>

              {/* SMS */}
              <div className="flex items-center justify-between">
                <div>
                  <h3 className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    Notifications par SMS
                  </h3>
                  <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    Recevez des SMS pour les alertes urgentes
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notificationSettings.smsNotifications}
                    onChange={(e) => setNotificationSettings(prev => ({
                      ...prev,
                      smsNotifications: e.target.checked
                    }))}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                </label>
              </div>

              {/* Push */}
              <div className="flex items-center justify-between">
                <div>
                  <h3 className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    Notifications push
                  </h3>
                  <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    Recevez des notifications sur votre navigateur
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notificationSettings.pushNotifications}
                    onChange={(e) => setNotificationSettings(prev => ({
                      ...prev,
                      pushNotifications: e.target.checked
                    }))}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                </label>
              </div>

              {/* Délai de rappel */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Délai de rappel (jours)
                </label>
                <input
                  type="number"
                  min="1"
                  max="30"
                  value={notificationSettings.reminderDays}
                  onChange={(e) => setNotificationSettings(prev => ({
                    ...prev,
                    reminderDays: parseInt(e.target.value) || 3
                  }))}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all ${
                    theme === 'dark' 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'border-gray-300 bg-white'
                  }`}
                />
              </div>

              <button
                onClick={handleSaveNotifications}
                disabled={loading}
                className="w-full flex items-center justify-center px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg hover:from-green-600 hover:to-emerald-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-all disabled:opacity-50"
              >
                <Bell className="w-5 h-5 mr-2" />
                {loading ? "Sauvegarde..." : "Sauvegarder les notifications"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientSettings;